import { useMerchantSettlements } from './hooks/useSettlements';
import { formatMinor } from '../../shared/utils/money';
import type { SettlementStatus } from './types';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_STYLES: Record<SettlementStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  SUCCESS: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

/** Gateway settlement history for a merchant — rendered on the merchant detail page. */
export function MerchantSettlements({ tenantId }: { tenantId: string }) {
  const { data: settlements = [], isLoading, error } = useMerchantSettlements(tenantId);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h2 className="text-base font-semibold text-foreground mb-4">Settlements</h2>

      {isLoading && <p className="text-sm text-foreground">Loading settlements…</p>}
      {error && <p className="text-sm text-red-500">Failed to load settlements.</p>}
      {!isLoading && !error && settlements.length === 0 && (
        <p className="text-sm text-foreground">No gateway settlements yet. Funds settle directly to the merchant under split-at-source.</p>
      )}

      {!isLoading && !error && settlements.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 font-semibold text-foreground">Provider</th>
              <th className="text-left py-2 font-semibold text-foreground">Amount</th>
              <th className="text-left py-2 font-semibold text-foreground">Status</th>
              <th className="text-left py-2 font-semibold text-foreground">Type</th>
              <th className="text-left py-2 font-semibold text-foreground">Settled</th>
              <th className="text-left py-2 font-semibold text-foreground">Settlement ID</th>
            </tr>
          </thead>
          <tbody>
            {settlements.map((s) => (
              <tr key={s.id} className="border-b border-border">
                <td className="py-2 text-foreground">{s.provider}</td>
                <td className="py-2 font-medium text-foreground">{formatMinor(s.amount, s.currency)}</td>
                <td className="py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[s.status] ?? 'bg-muted text-muted-foreground'}`}>
                    {s.status}
                  </span>
                </td>
                <td className="py-2 text-foreground">{s.isInternational ? 'International' : 'Local'}</td>
                <td className="py-2 text-foreground">{formatDate(s.settledAt)}</td>
                <td className="py-2 text-foreground font-mono text-xs max-w-[160px] truncate" title={s.providerSettlementId}>{s.providerSettlementId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
